/**
 * CorrectionReviewDialog — the "preview & confirm" step of a receipt
 * correction (fix mode on /sales-new).
 *
 * Owner 2026-09-04: the correction UI used to live inside the fixed bottom
 * bar, which grew with the preview and overflowed the screen (and covered
 * the totals column). It is now a checkout-skinned dialog: the page keeps
 * every piece of state and every handler (same split as CheckoutDialog);
 * this component only renders.
 *
 * Settle rules (owner rulings, same day):
 *   - refund      - cash-paid receipt reduced -> money back FROM THE DRAWER,
 *                   always. No digital refund: the digital money sits in the
 *                   bank, an in-app "send digitally" only creates a gap.
 *   - collect     - receipt grew past what was paid -> Cash / GCash / Maya /
 *                   Bank Transfer, routed like a normal sale (cash -> drawer
 *                   or active sub-drawer, the rest -> digital wallet).
 *   - on_account  - credit receipt: nothing to collect, nothing to refund;
 *                   show what moved and the balance before -> after.
 *   - even        - no money moves.
 *
 * Every `fix-*` data-testid from the in-bar panel is preserved.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Lock, Shield, Smartphone, Banknote } from 'lucide-react';
import { formatPHP } from '../lib/utils';
import DrawerTopUpPrompt from './DrawerTopUpPrompt';

export const FIX_COLLECT_METHODS = ['Cash', 'GCash', 'Maya', 'Bank Transfer'];

export default function CorrectionReviewDialog({
  open,
  onOpenChange,
  target,            // fixTarget - the receipt being corrected
  preview,           // fixPreview - the server's dry run
  branchId,
  // reason
  reason, setReason, reasonTouched, setReasonTouched,
  // collect
  collectMethod, setCollectMethod, collectRef, setCollectRef,
  // funds / pins
  fundsShort, setFundsShort,
  pinPromptOpen, pin, setPin, closedDayPin, setClosedDayPin,
  // commit
  commitBusy, indeterminate, onCommit,
}) {
  if (!preview) return null;

  const dir = preview.settle?.direction;
  const requires = preview.requires || {};
  const oldTotal = parseFloat(target?.grand_total) || 0;
  const newTotal = parseFloat(preview.grand_total) || 0;
  const paid = parseFloat(preview.amount_paid) || 0;
  const delta = Math.round((newTotal - oldTotal) * 100) / 100;
  const needsPin = requires.fix_pin || requires.closed_day_pin;
  const balBefore = preview.balance_before ?? Math.max(0, oldTotal - paid);
  const balAfter = preview.balance_after ?? Math.max(0, newTotal - paid);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md max-h-[92vh] overflow-y-auto"
        data-testid="fix-preview-result"
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }}>Confirm correction</DialogTitle>
          <DialogDescription>
            {target?.invoice_number} &middot; {target?.customer_name || 'Walk-in'} &middot; was {formatPHP(oldTotal)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* What will change - the server's own list */}
          {(preview.changes || []).length > 0 && (
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-xs">
              {preview.changes.map((c, k) => (
                <div key={k} className="px-3 py-1.5 text-slate-600">{c}</div>
              ))}
            </div>
          )}

          {/* The numbers - server-computed, never re-derived here */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 space-y-1">
            {dir === 'on_account' ? (
              <>
                <div className="flex justify-between"><span className="text-slate-500">Old total</span><b>{formatPHP(oldTotal)}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">New total</span><b>{formatPHP(newTotal)}</b></div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{delta < 0 ? 'Deducted' : 'Added'}</span>
                  <b className={delta < 0 ? 'text-amber-700' : 'text-slate-700'} data-testid="fix-settle-amount">{formatPHP(Math.abs(delta))}</b>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-1">
                  <span className="font-semibold text-slate-700">Balance on account</span>
                  <b data-testid="fix-balance-after">
                    {formatPHP(balBefore)}<span className="text-slate-400 font-normal"> &rarr; </span>{formatPHP(balAfter)}
                  </b>
                </div>
                <p className="text-[11px] text-slate-400 pt-1">Credit receipt &mdash; nothing is collected or refunded; the account balance moves.</p>
              </>
            ) : (
              <>
                <div className="flex justify-between"><span className="text-slate-500">New total</span><b>{formatPHP(newTotal)}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">Already paid</span><b>{formatPHP(paid)}</b></div>
                <div className="flex justify-between border-t border-slate-200 pt-1">
                  {dir === 'refund' && (
                    <><span className="font-semibold text-amber-700">Refund the customer</span>
                      <b className="text-amber-700" data-testid="fix-settle-amount">{formatPHP(preview.settle.amount)}</b></>
                  )}
                  {dir === 'collect' && (
                    <><span className="font-semibold text-emerald-700">Collect from the customer</span>
                      <b className="text-emerald-700" data-testid="fix-settle-amount">{formatPHP(preview.settle.amount)}</b></>
                  )}
                  {dir === 'even' && (
                    <><span className="font-semibold text-slate-600">Even &mdash; no money moves</span>
                      <b data-testid="fix-settle-amount">{formatPHP(0)}</b></>
                  )}
                </div>
              </>
            )}
          </div>

          {preview.paper_route === 'closed_day_carryover' && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex gap-2">
              <Lock size={14} className="shrink-0 mt-0.5" />
              <span>
                This receipt&apos;s business day is already Z-closed. The sealed report
                stays untouched &mdash; the adjustment posts on <b>today&apos;s</b> records,
                labeled as a carryover. This needs the tenant Admin PIN or the
                owner&apos;s authenticator code.
              </span>
            </div>
          )}

          {/* Refund - drawer cash, always (owner ruling 2026-09-04) */}
          {dir === 'refund' && (
            <div className="rounded-lg border border-slate-200 px-3 py-2 flex items-center gap-2 text-xs text-slate-600"
                 data-testid="fix-refund-cash">
              <Banknote size={14} className="text-emerald-700 shrink-0" />
              <span>Handed back as <b>drawer cash</b>. The cashier wallet is debited {formatPHP(preview.settle.amount)}.</span>
            </div>
          )}

          {/* Collect - same methods and routing as a sale */}
          {dir === 'collect' && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                {requires.walkin_collect
                  ? 'Walk-in receipt - the collect must complete for this fix to save:'
                  : 'Collect now:'}
              </p>
              <div className="flex gap-1.5 flex-wrap">
                {FIX_COLLECT_METHODS.map(m => (
                  <Button key={m} size="sm"
                    variant={collectMethod === m ? 'default' : 'outline'}
                    className={collectMethod === m ? 'bg-[#1A4D2E] hover:bg-[#143d24]' : ''}
                    onClick={() => setCollectMethod(m)}
                    data-testid={`fix-method-${m.replace(/\s/g, '')}`}>
                    {m === 'Cash' ? null : <Smartphone size={13} className="mr-1" />}{m}
                  </Button>
                ))}
              </div>
              {collectMethod !== 'Cash' && (
                <Input className="h-8" placeholder="Reference no. (optional)"
                  value={collectRef} onChange={e => setCollectRef(e.target.value)} />
              )}
            </div>
          )}

          {(requires.fix_pin_reasons || []).length > 0 && (
            <p className="text-xs text-amber-700 flex items-center gap-1">
              <Shield size={12} /> Manager PIN will be required: {requires.fix_pin_reasons.join(', ')}
            </p>
          )}

          <div className="space-y-1">
            <p className="text-xs text-slate-500">Reason for this correction *</p>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              onBlur={() => setReasonTouched(true)}
              placeholder="e.g. cashier keyed 5 instead of 3"
              rows={2}
              className={`w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 ${
                reasonTouched && !reason.trim()
                  ? 'border-red-300 focus:ring-red-200' : 'border-slate-200 focus:ring-indigo-200'
              }`}
              data-testid="fix-reason-input"
            />
            {reasonTouched && !reason.trim() && (
              <p className="text-[11px] text-red-500">A reason is required before committing.</p>
            )}
          </div>

          {fundsShort && (
            <DrawerTopUpPrompt
              detail={fundsShort}
              branchId={branchId}
              linkRef={target?.invoice_number || ''}
              onFunded={() => { setFundsShort(null); onCommit(); }}
              onCancel={() => setFundsShort(null)}
            />
          )}

          {pinPromptOpen && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 space-y-2" data-testid="fix-pin-prompt">
              {requires.fix_pin && (
                <div className="space-y-1">
                  <p className="text-xs text-slate-600 flex items-center gap-1">
                    <Shield size={12} /> Manager PIN
                    <span className="text-slate-400">&mdash; {(requires.fix_pin_reasons || []).join(', ')}</span>
                  </p>
                  <Input type="password" autoComplete="one-time-code" data-lpignore="true"
                    className="h-9" placeholder="Manager PIN" autoFocus
                    value={pin} onChange={e => setPin(e.target.value)}
                    data-testid="fix-pin-input" />
                </div>
              )}
              {requires.closed_day_pin && (
                <div className="space-y-1">
                  <p className="text-xs text-slate-600 flex items-center gap-1">
                    <Lock size={12} /> Closed day &mdash; tenant Admin PIN or authenticator code
                  </p>
                  <Input type="password" autoComplete="one-time-code" data-lpignore="true"
                    className="h-9" placeholder="Admin PIN / TOTP"
                    value={closedDayPin} onChange={e => setClosedDayPin(e.target.value)}
                    data-testid="fix-closedday-pin-input" />
                </div>
              )}
            </div>
          )}

          {/* Footer - same shape as the checkout confirm */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}
              data-testid="fix-dialog-back">
              Back to lines
            </Button>
            <Button
              className="flex-1 bg-[#1A4D2E] hover:bg-[#143d24] text-white"
              onClick={onCommit}
              disabled={commitBusy || !!fundsShort || indeterminate}
              data-testid="fix-commit-btn">
              {commitBusy ? 'Committing…'
                : needsPin ? (pinPromptOpen ? 'Authorize & commit' : 'Commit correction')
                : 'Commit correction'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
